import React, { useEffect, useMemo, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AppContext from "../utils/appContext";

const Navigation = () => {
  const context = useContext(AppContext);
  const [show, setShow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useState(JSON.parse(localStorage.getItem("user") || "{}"));

  const [navigation, setNavigation] = useState([
    { name: "Dashboard", href: "/" },
  ]);
  const [activePage, setActivePage] = useState("");

  useEffect(() => {
    setActivePage(location.pathname.split("/")[1]);
  }, [location]);

  const roles = useMemo(() => JSON.parse(user.groups || "[]"), [user.groups]);

  useEffect(() => {
    const newNavigation = [];

    if (process.env.REACT_APP_FILES === "true") {
      newNavigation.push({ name: "Files", href: "/files" });
    }

    if (process.env.REACT_APP_PRESS_RELEASE === "true") {
      newNavigation.push({ name: "Press releases", href: "/press-releases" });
    }
    if (roles.includes(0) || roles.includes(1)) {
      newNavigation.push({ name: "Users", href: "/users" });
    }
    if (
      process.env.REACT_APP_CONTACTS === "true" &&
      (roles.includes(0) || roles.includes(1))
    ) {
      newNavigation.push({ name: "Contacts", href: "/contacts" });
    }
    if (
      process.env.REACT_APP_EMAIL === "true" &&
      (roles.includes(0) || roles.includes(1))
    ) {
      newNavigation.push({ name: "Mail", href: "/email" });
    }

    if (roles.includes(0)) {
      newNavigation.push({ name: "Bin", href: "/bin" });
    }

    setNavigation(newNavigation);
    setShow(true);
  }, [roles]);

  const logout = () => {
    localStorage.clear();
    context?.updateLoginStatus(false);
    navigate("/login");
  };

  const isActive = (href: string) =>
    href === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(href);

  return (
    <>
      <div className="px-5 md:px-10 xl:px-16 2xl:px-20">
        {show && (
          <div className="container relative z-30 w-full py-5 mx-auto fade-in md:py-8">
            <div className="flex items-end justify-between gap-5 border-b border-border/35 pb-4">
              <div className="min-w-0">
                {process.env.REACT_APP_COMPANY_LOGO ? (
                  <button
                    className="text-2xl cursor-pointer"
                    onClick={() => navigate("/")}
                  >
                    <img
                      src={process.env.REACT_APP_COMPANY_LOGO}
                      className="max-h-6"
                      alt={process.env.REACT_APP_COMPANY_NAME || "Company logo"}
                    />
                  </button>
                ) : (
                  <>
                    <h1
                      onClick={() => navigate("/")}
                      className="brand-wordmark cursor-pointer text-[32px] leading-none md:text-[44px] lg:text-[56px]"
                    >
                      {process.env.REACT_APP_COMPANY_NAME}
                    </h1>
                    <p className="mt-2 hidden text-xs font-semibold uppercase text-muted md:block">
                      Brand asset library
                    </p>
                  </>
                )}
              </div>

              <nav className="items-end hidden lg:grid">
                <ul className="flex items-center justify-center lg:gap-8 xl:gap-10">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <button
                        onClick={() => {
                          navigate(item.href);
                          if ("/" + activePage === item.href) {
                            window.location.reload();
                          }
                        }}
                        className={`group relative cursor-pointer pb-1 text-sm font-semibold transition-all ${
                          isActive(item.href)
                            ? "text-text"
                            : "text-muted hover:text-text"
                        }`}
                      >
                        {item.name}
                        <span
                          className={`absolute left-0 -bottom-1 h-0.5 bg-accent transition-all ${
                            isActive(item.href)
                              ? "w-full"
                              : "w-0 group-hover:w-full"
                          }`}
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="items-center hidden gap-3 lg:flex">
                {user?.firstname ? (
                  <>
                    <p
                      className="hidden text-right text-xs font-semibold leading-5 text-muted md:block md:text-sm"
                      aria-hidden="true"
                    >
                      {user?.firstname} {user?.lastname}
                    </p>

                    <button
                      onClick={logout}
                      className="control-button flex h-10 w-10 cursor-pointer items-center justify-center rounded-control border text-sm leading-6 text-text hover:text-white"
                      aria-label="Log out"
                    >
                      <svg
                        className="w-4 h-4"
                        viewBox="0 0 8 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.26366 1.5929C6.43787 2.08954 7.2627 3.25252 7.2627 4.60768C7.2627 6.41455 5.79773 7.88076 3.98961 7.88076C2.18149 7.88076 0.717773 6.41455 0.717773 4.60768C0.717773 3.26625 1.52388 2.11449 2.67938 1.60912"
                          stroke="currentColor"
                          strokeWidth="0.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M3.99365 0.374512V4.73571"
                          stroke="currentColor"
                          strokeWidth="0.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => navigate("/login")}
                    className="control-button flex h-10 w-10 cursor-pointer items-center justify-center rounded-control border text-sm leading-6"
                    aria-label="Sign in"
                  >
                    <svg
                      className="w-5 h-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 lg:hidden">
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="control-button flex h-10 w-10 items-center justify-center rounded-control border outline-none"
                  aria-label="Toggle navigation"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {showMobileMenu ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 6h16M4 12h16m-7 6h7"
                      />
                    )}
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showMobileMenu && (
        <div className="fixed top-0 right-0 z-20 grid h-screen w-[min(360px,86vw)] content-center border-l border-white/10 bg-text p-8 text-white shadow-2xl">
          <nav>
            <ul className="grid items-center gap-5 text-right">
              {navigation.map((item) => (
                <li key={item.name}>
                  <button
                    onClick={() => {
                      navigate(item.href);
                      if ("/" + activePage === item.href) {
                        window.location.reload();
                      }
                    }}
                    className={`cursor-pointer text-2xl font-semibold transition-all ${
                      isActive(item.href)
                        ? "text-accent"
                        : "text-white/75 hover:text-white"
                    }`}
                  >
                    {item.name}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          <div className="flex items-center justify-end gap-2 mt-10 border-t border-white/15 pt-5">
            {user?.firstname ? (
              <>
                {" "}
                <p
                  className="text-right text-sm font-medium leading-6 text-white/70"
                  aria-hidden="true"
                >
                  {user?.firstname} {user?.lastname}
                </p>
                <button
                  onClick={logout}
                  className="flex cursor-pointer items-center justify-center rounded-control border border-white/20 p-3 text-sm leading-6 text-white/80 hover:border-accent hover:text-accent"
                  aria-label="Log out"
                >
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 8 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.26366 1.5929C6.43787 2.08954 7.2627 3.25252 7.2627 4.60768C7.2627 6.41455 5.79773 7.88076 3.98961 7.88076C2.18149 7.88076 0.717773 6.41455 0.717773 4.60768C0.717773 3.26625 1.52388 2.11449 2.67938 1.60912"
                      stroke="currentColor"
                      strokeWidth="0.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3.99365 0.374512V4.73571"
                      stroke="currentColor"
                      strokeWidth="0.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="flex cursor-pointer items-center justify-center rounded-control border border-white/20 p-3 text-sm leading-6 text-white/80 hover:border-accent hover:text-accent"
                aria-label="Sign in"
              >
                <svg
                  className="w-5 h-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
export default Navigation;
